<template>
    <ValidationObserver tag="form" ref="observer" @submit.prevent="submit()">
        <Card customClasses="tw:relative tw:mb-6" title="Detalles básicos">
            <div class="tw:absolute tw:top-6 tw:right-6 tw:flex tw:items-center tw:gap-2 tw:sm:gap-3">
                <span class="tw:text-xs tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                    {{ localForm.activa ? 'Activa' : 'Inactiva' }}
                </span>
                <button
                    type="button"
                    role="switch"
                    :aria-checked="localForm.activa"
                    :class="localForm.activa ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                    class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                    @click="localForm.activa = !localForm.activa"
                >
                    <span
                        :class="localForm.activa ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                        class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                    ></span>
                </button>
            </div>
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:md:gap-6 tw:gap-3 tw:mt-3">
                <div class="tw:flex tw:flex-col">
                    <BaseInputText
                        name="nombre"
                        label="Nombre de la campaña"
                        :rules="'required'"
                        placeholder="Ej. Campaña Playoff 2024"
                        autocomplete="off"
                        :value="localForm.nombre"
                        @input="localForm.nombre = $event"
                    />
                </div>
                <div class="tw:flex tw:flex-col">
                    <BaseMultiselect
                        name="promotor"
                        label="Promotor"
                        :options="promotores"
                        value-key="id"
                        label-key="label"
                        description="El promotor es la compañía."
                        :rules="'required'"
                        :value="localForm.promotor ? localForm.promotor.id : null"
                        @input="id => { localForm.promotor = promotores.find(p => p.id === id) || null }"
                    />                            
                </div>
            </div>
        </Card>

        <Card customClasses="tw:mb-6" title="Público objetivo (Segmentación)">
            <ValidationProvider name="segmentación" rules="required" v-slot="{ errors }">
                <input type="hidden" v-model="localForm.segmentacion" />
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-3 tw:md:gap-6 tw:mt-3" :data-validation-error="errors[0] ? '' : undefined">
                    <SelectionCard
                        :disabled="!localForm.promotor"
                        :active="localForm.segmentacion === 'eventos'"
                        :variant="errors[0] ? 'danger' : 'primary'"
                        title="Por eventos"
                        description="Basado en asistencia"
                        icon="material-icons"
                        @click="localForm.segmentacion = 'eventos'"
                    >
                        <template #icon="{ active }">
                            <i :class="['material-icons', active ? 'tw:text-white' : 'tw:text-gray-400']">event</i>
                        </template>
                    </SelectionCard>

                    <SelectionCard
                        :active="localForm.segmentacion === 'abonos'"
                        :variant="errors[0] ? 'danger' : 'primary'"
                        title="Por abonos"
                        description="Tipos de carnet"
                        icon="material-icons"
                        @click="() => { fetchAbonados(); localForm.segmentacion = 'abonos' }"
                    >
                        <template #icon="{ active }">
                            <i :class="['material-icons', active ? 'tw:text-white' : 'tw:text-gray-400']">card_membership</i>
                        </template>
                    </SelectionCard>

                    <SelectionCard
                        :active="localForm.segmentacion === 'deportistas'"
                        :variant="errors[0] ? 'danger' : 'primary'"
                        title="Fútbol base"
                        description="Cantera y equipos"
                        icon="material-icons"
                        @click="() => { fetchEquipos(); localForm.segmentacion = 'deportistas' }"
                    >
                        <template #icon="{ active }">
                            <i :class="['material-icons', active ? 'tw:text-white' : 'tw:text-gray-400']">sports_soccer</i>
                        </template>
                    </SelectionCard>
                </div>
            </ValidationProvider>

            <div v-if="localForm.segmentacion === 'eventos' && localForm.promotor" class="tw:mt-5">
                <BaseMultiselect
                    v-if="!loadingEventos"
                    name="eventos"
                    label="Eventos"
                    :options="eventos"
                    value-key="id"
                    label-key="name"
                    :rules="'required'"
                    :multiple="true"
                    :disabled="localForm.enviarATodosEventos"
                    :value="localForm.eventos.map(e => e.id)"
                    @input="ids => {
                        localForm.eventos = eventos.filter(e => ids.includes(e.id))
                    }"
                />
                <div v-else class="tw:flex tw:items-center tw:justify-start tw:h-10">
                    <p class="tw:text-sm tw:text-gray-400">Cargando eventos...</p>
                </div>
                <p class="tw:mt-2 tw:text-xs tw:text-gray-400">
                    Los eventos seleccionados reciben la notificación.
                </p>
                <div class="tw:mt-3 tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.enviarATodosEventos"
                        :class="localForm.enviarATodosEventos ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.enviarATodosEventos = !localForm.enviarATodosEventos"
                    >
                        <span
                            :class="localForm.enviarATodosEventos ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        Enviar a todos los eventos
                    </span>
                </div>
            </div>

            <div v-if="localForm.segmentacion === 'abonos'" class="tw:mt-5">
                <BaseMultiselect
                    v-if="!loadingAbonados"
                    name="abonados"
                    label="Tipos de abonados"
                    :options="abonados"
                    value-key="id"
                    label-key="label"
                    :multiple="true"
                    :rules="'required'"
                    :value="localForm.abonados.map(a => a.id)"
                    :disabled="localForm.enviarATodosAbonos"
                    @input="ids => {
                        localForm.abonados = abonados.filter(a => ids.includes(a.id))
                    }"
                />
                <div v-else class="tw:flex tw:items-center tw:justify-start tw:h-10">
                    <p class="tw:text-sm tw:text-gray-400">Cargando abonados...</p>
                </div>
                <p class="tw:mt-2 tw:text-xs tw:text-gray-400">
                    Los abonados seleccionados reciben la notificación basada en su categoría.
                </p>
                <div class="tw:mt-3 tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.enviarATodosAbonos"
                        :class="localForm.enviarATodosAbonos ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.enviarATodosAbonos = !localForm.enviarATodosAbonos"
                    >
                        <span
                            :class="localForm.enviarATodosAbonos ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        Enviar a todos los abonados
                    </span>
                </div>
            </div>

            <div v-if="localForm.segmentacion === 'deportistas'" class="tw:mt-5">
                <BaseMultiselect
                    v-if="!loadingEquipos"
                    name="equipos"
                    label="Equipos"
                    :options="equipos"
                    value-key="id"
                    label-key="nombre"
                    :multiple="true"
                    :rules="'required'"
                    :value="localForm.equipos.map(e => e.id)"
                    :disabled="localForm.enviarATodosEquipos"
                    @input="ids => {
                        localForm.equipos = equipos.filter(e => ids.includes(e.id))
                    }"
                />
                <div v-else class="tw:flex tw:items-center tw:justify-start tw:h-10">
                    <p class="tw:text-sm tw:text-gray-400">Cargando equipos...</p>
                </div>
                <p class="tw:mt-2 tw:text-xs tw:text-gray-400">
                    Los equipos seleccionados reciben la notificación basada en su categoría.
                </p>
                <div class="tw:mt-3 tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.enviarATodosEquipos"
                        :class="localForm.enviarATodosEquipos ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.enviarATodosEquipos = !localForm.enviarATodosEquipos"
                    >
                        <span
                            :class="localForm.enviarATodosEquipos ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        Enviar a todos los equipos
                    </span>
                </div>
            </div>
        </Card>

        <Card customClasses="tw:mb-6" title="Configuración del envío">
            <label class="tw:block tw:mb-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:mt-3">
                Retraso tras el trigger de compra
            </label>
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <Button
                    v-for="opcion in opcionesRetraso"
                    :key="opcion.value"
                    :variant="localForm.retraso === opcion.value ? 'border-active' : 'border'"
                    @click="localForm.retraso = opcion.value"
                >
                    {{ opcion.label }}
                </Button>
                <template v-if="localForm.retraso === 'personalizar'">
                    <BaseInputNumber
                        name="retraso"
                        rules="required"
                        customClass="tw:w-20"
                        :min="1"
                        :value="localForm.retrasoPersonalizado"
                        @input="localForm.retrasoPersonalizado = $event"
                    />
                    <BaseMultiselect
                        name="retrasoUnidad"
                        :options="[{ value: 'minutos', label: 'Minutos' }, { value: 'horas', label: 'Horas' }]"
                        value-key="value"
                        label-key="label"
                        :value="localForm.retrasoUnidad"
                        @input="localForm.retrasoUnidad = $event"
                    />
                </template>                        
            </div>
            <p class="tw:mt-3 tw:text-xs tw:text-gray-400">
                Tiempo de espera desde que el usuario realiza la compra hasta que se envía la comunicación.
            </p>
        </Card>

        <Card customClasses="tw:mb-6" title="Restricciones">
            <div class="tw:flex tw:flex-col tw:gap-4 tw:mt-3">
                <div class="tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.restriccionProductos"
                        :class="localForm.restriccionProductos ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.restriccionProductos = !localForm.restriccionProductos"
                    >
                        <span
                            :class="localForm.restriccionProductos ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        No enviar si el usuario ya ha comprado productos
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.restriccionParking"
                        :class="localForm.restriccionParking ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.restriccionParking = !localForm.restriccionParking"
                    >
                        <span
                            :class="localForm.restriccionParking ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        No enviar a usuarios que ya han comprado plaza de parking
                    </span>
                </div>                            
                <div class="tw:flex tw:items-center tw:gap-3">
                    <button
                        type="button"
                        role="switch"
                        :aria-checked="localForm.restriccionSeguros"
                        :class="localForm.restriccionSeguros ? 'tw:bg-blue-500' : 'tw:bg-gray-300'"
                        class="tw:relative tw:inline-flex tw:h-5 tw:w-9 tw:shrink-0 tw:cursor-pointer tw:rounded-full tw:transition-colors tw:duration-200"
                        @click="localForm.restriccionSeguros = !localForm.restriccionSeguros"
                    >
                        <span
                            :class="localForm.restriccionSeguros ? 'tw:translate-x-4' : 'tw:translate-x-0.5'"
                            class="tw:inline-block tw:h-4 tw:w-4 tw:self-center tw:rounded-full tw:bg-white tw:shadow tw:transition-transform tw:duration-200"
                        ></span>
                    </button>
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        No enviar a usuarios que ya han comprado seguro
                    </span>
                </div>
            </div>
        </Card>

        <Card customClasses="tw:mb-6" title="Canales de comunicación">
            <p class="tw:text-xs tw:text-gray-400 tw:mb-4">Selecciona los canales por los que quieres que se envíe esta comunicación.</p>
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mt-3">
                <SelectionCard
                    :active="localForm.canales.includes('whatsapp')"
                    variant="success"
                    title="WhatsApp Business"
                    description="Mensajería instantánea directa"
                    icon="fab fa-whatsapp"
                    @click="toggleCanal('whatsapp')"
                />
                <SelectionCard
                    :active="localForm.canales.includes('email')"
                    variant="primary"
                    title="Email Marketing"
                    description="Campaña de correo tradicional"
                    icon="fas fa-envelope"
                    @click="toggleCanal('email')"
                />
            </div>
            <div v-if="localForm.canales.length === 0" class="tw:mt-4 tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3">
                <i class="material-icons tw:text-red-500 tw:mt-0.5">warning</i>
                <p class="tw:text-sm tw:text-red-600 tw:m-0">
                    Debes seleccionar al menos un canal de comunicación para programar la campaña.
                </p>
            </div>
        </Card>

        <Card v-if="localForm.canales.includes('whatsapp')" customClasses="tw:mb-6" title="Contenido WhatsApp">
            <div class="tw:flex tw:items-center tw:justify-end tw:mb-2">
                <a
                    v-if="localForm.plantilla"
                    href="#"
                    class="tw:text-sm tw:text-blue-500 tw:flex tw:items-center tw:gap-1"
                    @click.prevent="previewModalVisible = true"
                >
                    <i class="material-icons tw:text-blue-500">visibility</i> Ver vista previa
                </a>
            </div>
            <BaseMultiselect
                name="plantilla"
                label="Seleccionar plantilla existente"
                :options="plantillas"
                value-key="id"
                label-key="label"
                :rules="'required'"
                :value="localForm.plantilla ? localForm.plantilla.id : null"
                @input="id => {
                    localForm.plantilla = plantillas.find(p => p.id === id) || null
                }"
            />
            <b-modal
                v-model="previewModalVisible"
                title="Vista previa"
                centered
            >
                <VistaPreviaComponent
                    v-if="localForm.plantilla"
                    :components="localForm.plantilla.components || ''"
                />
                <template #modal-footer>
                    <Button @click="previewModalVisible = false">
                        Cerrar
                    </Button>
                </template>
            </b-modal>
            <div class="tw:mt-4 tw:flex tw:items-center tw:gap-2 tw:rounded tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-3">
                <i class="material-icons tw:text-blue-500 tw:mt-0.5">info</i>
                <p class="tw:text-sm tw:text-blue-600 tw:m-0">
                    Estas campañas tendrán un coste de <b>{{ teatro.coste_envio_whatsapp_marketing | formatear_precio }}</b> por mensaje enviado.
                </p>
            </div>
        </Card>

        <Card v-if="localForm.canales.includes('email')" customClasses="tw:mb-6" title="Contenido Email">
            <div class="tw:flex tw:flex-col tw:gap-4 tw:mt-3">
                <div class="tw:flex tw:flex-col">
                    <BaseInputText
                        name="email_subject"
                        label="Asunto del email"
                        :rules="'required'"
                        placeholder="Ej. Oferta especial para ti"
                        autocomplete="off"
                        :value="localForm.email_subject"
                        @input="localForm.email_subject = $event"
                    />
                </div>
                <div class="tw:flex tw:flex-col">
                    <label class="tw:block tw:mb-2 tw:text-xs tw:font-semibold tw:text-gray-700 tw:uppercase tw:tracking-wide">
                        Contenido del email
                    </label>
                    <Button
                        type="button"
                        variant="border"
                        class="tw:self-start tw:mb-2"
                        @click="showPreview = !showPreview"
                    >
                        {{ showPreview ? 'Ocultar previsualización' : 'Ver previsualización' }}
                    </Button>
                    <ValidationProvider name="contenido email" rules="required" v-slot="{ errors }">
                        <textarea
                            v-model="localForm.email_content"
                            rows="12"
                            style="font-family:monospace;width:100%"
                            :class="{ 'tw:border tw:border-red-500 tw:rounded': errors[0] }"
                        ></textarea>
                        <span v-if="errors[0]" class="tw:text-xs tw:text-red-500 tw:mt-1">{{ errors[0] }}</span>
                    </ValidationProvider>
                    <iframe
                        v-if="showPreview"
                        :srcdoc="localForm.email_content"
                        sandbox="allow-same-origin"
                        style="width:100%;height:600px;border:1px solid #ccc;margin-top:0.5rem"
                    ></iframe>
                </div>
            </div>
        </Card>

        <div class="tw:mt-6 tw:flex tw:flex-col tw:items-end">
            <Button
                type="submit"
                variant="primary"
                :loading="loading"
                :disabled="formHasErrors || localForm.canales.length === 0"
                icon="material-icons"
            >
                <span class="tw:flex tw:items-center tw:gap-2">
                    <i class="material-icons">schedule</i>
                    {{ esEdicion ? 'Actualizar campaña' : 'Programar campaña' }}
                </span>
            </Button>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, watch, getCurrentInstance, nextTick } from 'vue';
    import { useFormUpselling } from '@/composables/admin/upselling/useFormUpselling'
    import VistaPreviaComponent from '@/components/backend/plantillas-whatsapp/VistaPreviaComponent'
    import SelectionCard from '@/modules/admin/ui/SelectionCard.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText'
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect'
    import Card from '@/modules/admin/ui/Card'
    import Button from '@/modules/admin/ui/Button'

    const emit = defineEmits(['terminado'])
    const props = defineProps({
        initialForm: {
            type: Object,
            required: true
        }
    })
    const localForm = ref({ ...props.initialForm })
    const showPreview = ref(false)
    const previewModalVisible = ref(false)
    const esEdicion = computed(() => !!localForm.value.id)
    const { proxy } = getCurrentInstance()

    const {
        observer,
        loading,
        loadingEventos,
        loadingAbonados,
        loadingEquipos,
        teatro,
        eventos,
        promotores,
        abonados,
        plantillas,
        equipos,
        opcionesRetraso,
        validationErrorMsg,
        validationFailedAt,
        submit,
        formHasErrors,
        fetchAbonados,
        fetchEquipos,
        toggleCanal
    } = useFormUpselling(emit, localForm)

    watch(validationErrorMsg, msg => {
        if (msg) {
            proxy.$toast.error(msg)
            validationErrorMsg.value = null
        }
    })

    watch(validationFailedAt, async() => {
        if (validationFailedAt.value) {
            await nextTick()
            setTimeout(() => {
                const firstError = document.querySelector('.is-invalid, [data-validation-error]')
                if (firstError) {
                    proxy.$scrollTo(firstError, 300, {
                        offset: -(window.innerHeight / 3)
                    })
                }
            }, 50)
        }
    })

</script>

<style scoped>
    .custom-multiselect ::v-deep(.multiselect__tags) {
        min-height: 62px;
        display: flex;
        align-items: center;
    }

</style>
